import { INDIAN_STATES, isPincode } from "@shared/types";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { FieldInput, FieldSelect } from "@/features/studio/theme";
import { FieldGroupLabel, FieldHint } from "./field-label";

export interface LocationValue {
	city: string;
	state: string;
	pincode: string;
}

export const EMPTY_LOCATION: LocationValue = {
	city: "",
	state: "",
	pincode: "",
};

/** The message key for a location that cannot be saved, or null when it can. */
export function locationErrorKey(location: LocationValue): string | null {
	if (!location.city.trim() || !location.state) {
		return "onboarding.fields.cityStateRequired";
	}
	if (location.pincode.trim() && !isPincode(location.pincode.trim())) {
		return "onboarding.fields.pincodeInvalid";
	}
	return null;
}

/** The wire shape: blanks become null so a cleared field clears the column. */
export function locationPayload(location: LocationValue): {
	city: string | null;
	state: string | null;
	pincode: string | null;
} {
	return {
		city: location.city.trim() || null,
		state: location.state || null,
		pincode: location.pincode.trim() || null,
	};
}

/** Pincode input that only takes digits, up to six. */
export function PincodeInput({
	value,
	onChange,
	label,
	hint,
}: {
	value: string;
	onChange: (value: string) => void;
	label?: string;
	hint?: string;
}) {
	const { t } = useTranslation();
	const id = useId();
	const hintId = useId();
	const invalid = value.length > 0 && !isPincode(value);
	return (
		<div>
			<FieldInput
				id={id}
				label={label ?? t("onboarding.fields.pincode")}
				value={value}
				inputMode="numeric"
				autoComplete="postal-code"
				maxLength={6}
				aria-invalid={invalid || undefined}
				aria-describedby={invalid || hint ? hintId : undefined}
				onChange={(event) => onChange(event.target.value.replace(/\D/g, ""))}
				placeholder={t("onboarding.fields.pincodePlaceholder")}
			/>
			{invalid ? (
				<FieldHint id={hintId} tone="error">
					{t("onboarding.fields.pincodeInvalid")}
				</FieldHint>
			) : (
				hint && <FieldHint id={hintId}>{hint}</FieldHint>
			)}
		</div>
	);
}

/** City, state and optional pincode, stacked on a phone and in a row from `sm`. */
export function LocationFields({
	label,
	value,
	onChange,
}: {
	label: string;
	value: LocationValue;
	onChange: (next: LocationValue) => void;
}) {
	const { t } = useTranslation();
	const cityId = useId();
	const stateId = useId();
	return (
		<fieldset className="min-w-0">
			<FieldGroupLabel as="legend">{label}</FieldGroupLabel>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
				<FieldInput
					id={cityId}
					label={t("onboarding.fields.city")}
					value={value.city}
					autoComplete="address-level2"
					maxLength={100}
					onChange={(event) => onChange({ ...value, city: event.target.value })}
					placeholder={t("onboarding.fields.cityPlaceholder")}
				/>
				<FieldSelect
					id={stateId}
					label={t("onboarding.fields.state")}
					value={value.state || "__none__"}
					onChange={(event) =>
						onChange({
							...value,
							state:
								event.target.value === "__none__" ? "" : event.target.value,
						})
					}
					options={[
						{ value: "__none__", label: t("onboarding.fields.selectState") },
						...INDIAN_STATES.map((state) => ({ value: state, label: state })),
					]}
				/>
				<PincodeInput
					value={value.pincode}
					onChange={(pincode) => onChange({ ...value, pincode })}
				/>
			</div>
		</fieldset>
	);
}
